import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  applyBid, applyChooseTrump, applyHandToGame, applyPlay,
  canUndoSoloPlay, cardsPlayedFromLogForSeat, displayMatchScores, projectedMatchWinner,
  legalBids, legalPlays, canClaimCoke, applyClaimCokeLocal,
  undoSoloPlay,
} from "../engine/engine";
import { botBid, botChooseTrump, botPlay, Difficulty, normalizeDifficulty } from "../ai/bot";
import { useAuth } from "../hooks/useAuth";
import { callRecordSoloStats } from "../lib/functions";
import { startQuickPlay } from "../game/quickPlay";
import {
  clearQuickPlaySession,
  loadQuickPlaySession,
  saveQuickPlaySession,
} from "../game/quickPlaySession";
import {
  BidPill, DealAnimation, FourSeatTrickLayout, HAND_FAN_DEAL_SPACER_CLASS, HandFan, HandResultCenter, ScoreBadge,
  SUIT_LABEL, SUIT_SYMBOL, TRUMP_CHOICE_BUTTON_CLASS, TrickPlaysAtSeats, TrumpSuitHero, TurnChip, rankLabel,
} from "../components/cards";
import {
  BidValue, Card, DEFAULT_RULESET, GameState, HandState, Ruleset, SeatIndex, Suit, TEAM_OF, Trick,
} from "../types/game";
import { RULESET_INFO } from "../content/rules";

const BOT_DELAY_MS = 650;
// Time the 4 cards sit in the center, winner ringed, before they start
// flying off toward the winning seat.
const TRICK_FREEZE_MS = 1500;
/** Sixth trick → hand ends; linger slightly longer so the table doesn’t flash to the score panel. */
const TRICK_FREEZE_LAST_MS = 2600;
// Extra time the frozen-trick label ("Trick won by X") lingers after the
// cards have flown away. Kept a touch longer than the exit animation so
// the fly-out has time to complete.
// ~matches PlayedCard flight-out (~450ms) with buffer; all seats animate together.
const TRICK_EXIT_MS = 700;

interface LocationState {
  /** Present only when navigating from Home "Quick play"; refresh drops this so we restore from sessionStorage. */
  fromHomeQuickPlay?: true;
  partnerDifficulty?: Difficulty;
  opponentDifficulty?: Difficulty;
  ruleset?: Ruleset;
}

export default function PlayPage() {
  const { user, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const locState = location.state as LocationState | null;
  const partnerDifficulty: Difficulty = locState?.partnerDifficulty ?? "advanced";
  const opponentDifficulty: Difficulty = locState?.opponentDifficulty ?? "advanced";
  const rulesetFromNav: Ruleset = locState?.ruleset ?? DEFAULT_RULESET;

  const [game, setGame] = useState<GameState | null>(null);

  // One bootstrap per navigation entry: Home sets `fromHomeQuickPlay`; a full reload
  // clears router state → restore from sessionStorage (same tab/session).
  const bootKeyRef = useRef("");
  useEffect(() => {
    if (!user || !profile) return;
    const bk = `${user.uid}:${location.key}`;
    if (bootKeyRef.current === bk) return;
    bootKeyRef.current = bk;

    const cfg = {
      userUid: user.uid,
      userScreenName: profile.screenName,
      partnerDifficulty,
      opponentDifficulty,
      ruleset: rulesetFromNav,
    };

    if (locState?.fromHomeQuickPlay === true) {
      clearQuickPlaySession();
      setGame(startQuickPlay(cfg));
      return;
    }

    const restored = loadQuickPlaySession(user.uid);
    if (restored) {
      setGame(restored);
      return;
    }

    setGame(startQuickPlay(cfg));
  }, [
    user?.uid,
    profile?.screenName,
    location.key,
    locState?.fromHomeQuickPlay,
    partnerDifficulty,
    opponentDifficulty,
    rulesetFromNav,
  ]);

  useEffect(() => {
    if (!game || !user) return;
    saveQuickPlaySession(user.uid, game);
  }, [game, user]);

  // Persist solo quick-play stats to Firestore (server-only writes via callable).
  const soloStatsRecordedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!user?.uid || !game) return;
    const hand = game.currentHand;

    if (hand.phase === "done") {
      const handKey = `${game.seed}-hand-${hand.handNumber}`;
      if (!soloStatsRecordedRef.current.has(handKey)) {
        soloStatsRecordedRef.current.add(handKey);
        void callRecordSoloStats({
          idempotencyKey: handKey,
          hand: {
            phase: hand.phase,
            score: hand.score
              ? {
                  teamA: hand.score.teamA,
                  teamB: hand.score.teamB,
                  bidderMade: hand.score.bidderMade,
                }
              : undefined,
            abandoned: hand.abandoned,
            bidder: hand.bidder,
            bid: hand.bid,
          },
        }).catch(err => {
          soloStatsRecordedRef.current.delete(handKey);
          console.error("recordSoloStats hand failed", err);
        });
      }
    }

    const gameWinner = game.winner ?? projectedMatchWinner(game);
    if (gameWinner) {
      const gameKey = `${game.seed}-game`;
      if (!soloStatsRecordedRef.current.has(gameKey)) {
        soloStatsRecordedRef.current.add(gameKey);
        void callRecordSoloStats({
          idempotencyKey: gameKey,
          gameWinner,
        }).catch(err => {
          soloStatsRecordedRef.current.delete(gameKey);
          console.error("recordSoloStats game failed", err);
        });
      }
    }
  }, [game, user?.uid]);

  // Freeze the center-table display when a trick just completed so the user
  // can see all four cards + who won, then sweep the cards off toward the
  // winning seat. Two timed phases:
  //   phase 1 (frozenTrick + trickCardsVisible=true): cards sit for
  //     TRICK_FREEZE_MS, "Trick won by X" label shows below.
  //   phase 2 (frozenTrick + trickCardsVisible=false): cards fly to the
  //     winner via PlayedCard's flight animation. Label still shows.
  //   phase 3 (frozenTrick=null): next trick's display takes over.
  // Same cleanup applies to the 6th/final trick — the hand-complete panel
  // below lets the user read the score for as long as they want.
  const [frozenTrick, setFrozenTrick] = useState<Trick | null>(null);
  const [trickCardsVisible, setTrickCardsVisible] = useState(true);
  const prevTricksRef = useRef<{ handNum: number; count: number }>({ handNum: 0, count: 0 });
  // LayoutEffect: latch the completed trick in the same paint as when the engine
  // clears `currentTrick.plays` — avoids one empty-table frame before freeze mounts.
  // Timers live in useEffect below so React Strict Mode’s layout double-invoke
  // doesn’t cancel timeouts before fly-off runs.
  useLayoutEffect(() => {
    if (!game) return;
    const hand = game.currentHand;
    const prev = prevTricksRef.current;
    const cur = { handNum: hand.handNumber, count: hand.tricks.length };
    const handChanged = cur.handNum !== prev.handNum;
    const justFinishedTrick = !handChanged && cur.count > prev.count;
    prevTricksRef.current = cur;

    if (handChanged) {
      setFrozenTrick(null);
      setTrickCardsVisible(true);
      return;
    }
    if (justFinishedTrick) {
      setFrozenTrick(hand.tricks[hand.tricks.length - 1]);
      setTrickCardsVisible(true);
    }
  }, [game?.currentHand.handNumber, game?.currentHand.tricks.length]);

  useEffect(() => {
    if (!game || !frozenTrick) return;
    const nTricks = game.currentHand.tricks.length;
    const isLastTrick = nTricks === 6;
    const freezeMs = isLastTrick ? TRICK_FREEZE_LAST_MS : TRICK_FREEZE_MS;
    const t1 = window.setTimeout(() => setTrickCardsVisible(false), freezeMs);
    const t2 = window.setTimeout(() => {
      setFrozenTrick(null);
      setTrickCardsVisible(true);
    }, freezeMs + TRICK_EXIT_MS);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [game?.currentHand.handNumber, frozenTrick]);

  // Deal animation: fires once whenever a new hand starts (detected by a
  // handNumber transition into a fresh-bidding state). While the deal is
  // playing, bots hold off, the hand fan stays hidden, and the bidding
  // panel is suppressed so the player can watch the cards fly out.
  const lastDealtHandRef = useRef<number>(-1);
  const [dealingForHand, setDealingForHand] = useState<number | null>(null);
  // LayoutEffect (not Effect): `applyHandToGame` commits the new dealt hands in the
  // same paint as bidding — an Effect ran after paint, so the fan flashed briefly
  // before `isDealing` hid it. Running before paint removes that flash.
  useLayoutEffect(() => {
    if (!game) return;
    const h = game.currentHand;
    const isFreshHand =
      h.phase === "bidding" && h.bids.length === 0 && h.tricks.length === 0;
    if (isFreshHand && h.handNumber !== lastDealtHandRef.current) {
      lastDealtHandRef.current = h.handNumber;
      setDealingForHand(h.handNumber);
    }
  }, [
    game?.currentHand.handNumber,
    game?.currentHand.phase,
    game?.currentHand.bids.length,
    game?.currentHand.tricks.length,
  ]);
  const ch = game?.currentHand;
  const isFreshBiddingHand =
    !!ch &&
    ch.phase === "bidding" &&
    ch.bids.length === 0 &&
    ch.tricks.length === 0;
  const isDealing =
    !!game &&
    !!ch &&
    isFreshBiddingHand &&
    (dealingForHand === ch.handNumber ||
      ch.handNumber !== lastDealtHandRef.current);

  // Auto-advance bots whenever it becomes a bot's turn.
  const advancingRef = useRef(false);
  useEffect(() => {
    if (!game || game.winner) return;
    // Hold bots during the deal animation so no one bids before the last
    // card has landed.
    if (isDealing) return;
    // Never let a bot play into a frozen trick — wait for the reveal to finish.
    if (frozenTrick && game.currentHand.phase === "playing") return;
    const hand = game.currentHand;
    const seat = currentActor(hand);
    if (seat === null) {
      // Hand is done (or game is over). In quick play we let the user
      // drive the pace — they click the Next-hand button when ready.
      return;
    }
    const seatPlayer = game.players.find(p => p.seat === seat);
    const isBot = !!seatPlayer?.botDifficulty;
    if (!isBot) return;

    if (advancingRef.current) return;
    advancingRef.current = true;
    const t = setTimeout(() => {
      advancingRef.current = false;
      // Re-read the actor from latest state so a stale timeout never calls
      // applyPlay for the wrong seat (e.g. after solo undo back to human).
      setGame(g => {
        if (!g) return g;
        const actor = currentActor(g.currentHand);
        if (actor === null) return g;
        const p = g.players.find(pl => pl.seat === actor);
        if (!p?.botDifficulty) return g;
        return stepBot(g, actor);
      });
    }, BOT_DELAY_MS);
    return () => {
      advancingRef.current = false;
      clearTimeout(t);
    };
  }, [game, frozenTrick, isDealing]);

  if (!user) return <Navigate to="/" replace />;
  if (!profile) return <Navigate to="/" replace />;
  if (!game) return null;

  const hand = game.currentHand;
  const winnerTeam = projectedMatchWinner(game);
  const humanSeat: SeatIndex = 0;
  const playersBySeat = Object.fromEntries(game.players.map(p => [p.seat, p])) as Record<
    SeatIndex,
    GameState["players"][number]
  >;

  function onHumanBid(value: BidValue | "pass") {
    setGame(g => {
      if (!g) return g;
      const next = applyBid(g.currentHand, { seat: humanSeat, value }, g.ruleset);
      return { ...g, currentHand: next };
    });
  }

  function onHumanClaimCoke() {
    setGame(g => {
      if (!g) return g;
      if (!canClaimCoke(g.currentHand, humanSeat, g.ruleset)) return g;
      return { ...g, currentHand: applyClaimCokeLocal(g.currentHand, humanSeat, g.ruleset) };
    });
  }

  function onHumanTrump(suit: Suit) {
    setGame(g => {
      if (!g) return g;
      const next = applyChooseTrump(g.currentHand, humanSeat, suit);
      return { ...g, currentHand: next };
    });
  }

  function onHumanPlay(card: Card) {
    setGame(g => {
      if (!g) return g;
      const next = applyPlay(g.currentHand, humanSeat, card, g.ruleset);
      return { ...g, currentHand: next };
    });
  }

  function onUndoPlay() {
    setGame(g => {
      if (!g || !canUndoSoloPlay(g.currentHand, humanSeat)) return g;
      return { ...g, currentHand: undoSoloPlay(g.currentHand, humanSeat) };
    });
  }

  const showUndoPlay =
    hand.phase === "playing" &&
    !isDealing &&
    canUndoSoloPlay(hand, humanSeat);

  function quitToHome() {
    clearQuickPlaySession();
    navigate("/", { replace: true });
  }

  function dealNextHand() {
    setGame(g => {
      if (!g || g.currentHand.phase !== "done" || g.winner) return g;
      return applyHandToGame(g);
    });
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-3 sm:py-6">
      <TopBar
        game={game}
        playersBySeat={playersBySeat}
        onQuit={quitToHome}
      />

      <div className="relative">
        <div className="relative mb-1 sm:mb-1.5 px-1 sm:px-0">
          <div className="flex justify-center">
            <SeatLabel
              label="Partner"
              name={playersBySeat[2].screenName}
              difficulty={playersBySeat[2].botDifficulty}
              active={hand.turn === 2}
              dealer={hand.dealer === 2}
              compact
              className="max-w-[10rem] w-full shrink-0"
            />
          </div>
          {hand.trump ? (
            <div className="absolute right-0 top-0 flex flex-col items-center leading-none pointer-events-none select-none sm:right-1">
              <span className="text-[8px] uppercase tracking-wide text-white/40 mb-0.5">Trump</span>
              <TrumpSuitHero suit={hand.trump} />
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-[minmax(0,4.75rem)_minmax(0,1fr)_minmax(0,4.75rem)] sm:grid-cols-[minmax(0,5.5rem)_2fr_minmax(0,5.5rem)] gap-2 sm:gap-4 items-center mt-1">
          <SeatLabel
            label="Opp. (L)"
            name={playersBySeat[1].screenName}
            difficulty={playersBySeat[1].botDifficulty}
            active={hand.turn === 1}
            dealer={hand.dealer === 1}
            narrow
          />
          <TrickArea
            hand={hand}
            players={playersBySeat}
            frozenTrick={frozenTrick}
            trickCardsVisible={trickCardsVisible}
            matchBanner={
              winnerTeam ? (winnerTeam === "A" ? "You win!" : "You lost.") : undefined
            }
          />
          <SeatLabel
            label="Opp. (R)"
            name={playersBySeat[3].screenName}
            difficulty={playersBySeat[3].botDifficulty}
            active={hand.turn === 3}
            dealer={hand.dealer === 3}
            narrow
          />
        </div>

        {/* Bid / trump sit under the table (above the hand) so they stay on-screen on phones. */}
        {!isDealing && hand.phase === "bidding" && hand.turn === humanSeat && (
          <div className="mt-2">
            <BiddingPanel
              hand={hand}
              seat={humanSeat}
              ruleset={game.ruleset}
              onBid={onHumanBid}
              onClaimCoke={onHumanClaimCoke}
              compact
            />
          </div>
        )}
        {hand.phase === "choosingTrump" && hand.bidder === humanSeat && (
          <div className="mt-2">
            <TrumpPanel onPick={onHumanTrump} compact />
          </div>
        )}
        {!isDealing && hand.phase === "done" && !winnerTeam && (
          <div className="mt-2">
            <HandScorePanel game={game} onNext={dealNextHand} />
          </div>
        )}

        {/* Unmount the fan during deal — opacity fades still painted stale cards underneath a
            transparent DealAnimation overlay for a frame before flying cards existed. */}
        <div className="mt-2 sm:mt-3">
          {!isDealing ? (
            <YourHand
              hand={hand}
              seat={humanSeat}
              ruleset={game.ruleset}
              onPlay={onHumanPlay}
              showUndo={showUndoPlay}
              onUndo={onUndoPlay}
            />
          ) : (
            <div className={HAND_FAN_DEAL_SPACER_CLASS} aria-hidden />
          )}
        </div>

        {isDealing && (
          <DealAnimation
            dealerSlot={hand.dealer}
            onDone={() => setDealingForHand(null)}
          />
        )}
      </div>

      {winnerTeam && (
        <div className="mt-2 sm:mt-3">
          <GameOverPanel
            onPlayAgain={() => {
              setGame(startQuickPlay({
                userUid: user.uid,
                userScreenName: profile.screenName,
                partnerDifficulty,
                opponentDifficulty,
                ruleset: game.ruleset,
              }));
            }}
            onHome={quitToHome}
          />
        </div>
      )}

      <EventLogPeek hand={hand} players={playersBySeat} />
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function TopBar({
  game,
  playersBySeat,
  onQuit,
}: {
  game: GameState;
  playersBySeat: Record<SeatIndex, GameState["players"][number]>;
  onQuit: () => void;
}) {
  const { scoreA: showA, scoreB: showB } = displayMatchScores(game);
  const hand = game.currentHand;
  const phaseLabel = {
    deal: "Dealing",
    bidding: "Bidding",
    choosingTrump: "Choosing trump",
    playing: "Playing",
    scoring: "Scoring",
    done: "Hand complete",
  }[hand.phase];
  const rulesetLabel = RULESET_INFO[game.ruleset ?? DEFAULT_RULESET].shortLabel;

  return (
    <div className="mb-2 sm:mb-3">
      <div className="flex items-center justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <ScoreBadge label="You" score={showA} highlight />
          <ScoreBadge label="Opp." score={showB} />
        </div>
        <div className="shrink-0">
          <button
            type="button"
            onClick={onQuit}
            className="text-sm text-white/60 hover:text-white border border-white/15 rounded-lg px-3 py-1.5"
          >
            Quit
          </button>
        </div>
      </div>
      <div className="mt-1.5 text-[10px] sm:text-xs text-white/60 text-center sm:text-left leading-snug">
        <span>
          Hand {hand.handNumber} · {phaseLabel}
        </span>
        <span className="text-[9px] sm:text-[10px] uppercase tracking-wide text-white/35 ml-1.5">
          {rulesetLabel}
        </span>
        {!hand.trump ? (
          <>
            <span className="mx-1.5 text-white/25">·</span>
            <span className="text-white/40">Trump: —</span>
          </>
        ) : null}
        {hand.bid && hand.bidder !== undefined && (
          <>
            {" "}
            <span className="mx-1.5 text-white/25">·</span>
            Bid {hand.bid} ·{" "}
            {(() => {
              if (hand.bidder === 0) return "you";
              const name = playersBySeat[hand.bidder]?.screenName?.trim();
              return name || `seat ${hand.bidder}`;
            })()}
          </>
        )}
      </div>
    </div>
  );
}

function SeatLabel({
  label, name, difficulty, active, dealer, className = "", compact, narrow,
}: {
  label: string;
  name: string;
  difficulty?: Difficulty;
  active?: boolean;
  dealer?: boolean;
  className?: string;
  /** Shorter partner row — bot shown as “Name (bot)”. */
  compact?: boolean;
  /** Narrower opponent column. */
  narrow?: boolean;
}) {
  const displayName = difficulty ? `${name} (bot)` : name;
  const pad = compact ? "px-2 py-1" : narrow ? "px-1.5 py-1.5" : "px-3 py-2";
  const labelSize = compact || narrow ? "text-[10px]" : "text-xs";
  return (
    <div
      className={
        "relative rounded-lg text-sm text-center border transition-shadow w-full " +
        pad + " " +
        (active
          ? "bg-emerald-500/20 border-emerald-400/60 text-white " +
            "shadow-[0_0_18px_rgba(52,211,153,0.45)] animate-[pulse_2s_ease-in-out_infinite]"
          : "bg-white/5 border-white/10 text-white/70") +
        (className ? " " + className : "")
      }
    >
      <div className={"uppercase tracking-wide text-white/40 " + labelSize}>{label}</div>
      <div className="font-semibold truncate text-xs leading-tight">{displayName}</div>
      <div className="mt-0.5 flex items-center justify-center gap-1 flex-wrap">
        {dealer && (
          <span className="text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full border border-amber-300/60 bg-amber-400/20 text-amber-100">
            Dealer
          </span>
        )}
        {active && (
          <span className="text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full border border-emerald-300/60 bg-emerald-400/20 text-emerald-100">
            Next
          </span>
        )}
      </div>
    </div>
  );
}

function TrickArea({
  hand, players, frozenTrick, trickCardsVisible, matchBanner,
}: {
  hand: HandState;
  players: Record<SeatIndex, GameState["players"][number]>;
  frozenTrick: Trick | null;
  // When false during a frozen trick, played cards animate off via `PlayedCard`.
  trickCardsVisible: boolean;
  /** Match over — headline in the upper table area (matches former GameOver title style). */
  matchBanner?: string;
}) {
  // While a trick is being revealed, show those four cards (with the winner
  // ringed) in place of whatever the engine has moved on to.
  const displayTrick = frozenTrick ?? hand.currentTrick;
  const plays = displayTrick?.plays ?? [];
  const winnerSeat = frozenTrick?.winner;
  const hideFrozenCards = frozenTrick && !trickCardsVisible;

  const inBidding = hand.phase === "bidding";
  const bidBySeat: Partial<Record<SeatIndex, BidValue | "pass">> = {};
  for (const b of hand.bids) bidBySeat[b.seat] = b.value;
  const highBid = hand.bids.reduce<{ seat: SeatIndex; value: BidValue } | null>((best, b) => {
    if (b.value === "pass") return best;
    const v = b.value as BidValue;
    if (!best || v > best.value) return { seat: b.seat, value: v };
    return best;
  }, null);

  const nameFor = (seat: SeatIndex) =>
    seat === 0 ? "You" : players[seat]?.screenName?.trim() || `Seat ${seat}`;

  const cokedSeats = new Set(hand.cokeClaimed ?? []);
  // PlayPage always shows seat 0 at the bottom, seat 1 left, seat 2 top,
  // seat 3 right. Cards slide in from that direction so plays read as
  // coming "from" each player's spot on the table.
  const SEAT_DIR: Record<SeatIndex, "top" | "bottom" | "left" | "right"> = {
    0: "bottom",
    1: "left",
    2: "top",
    3: "right",
  };
  const winnerDir =
    winnerSeat !== undefined ? SEAT_DIR[winnerSeat] : undefined;
  const renderBidSeat = (seat: SeatIndex) => {
    const showDealerHere = seat === 0 && hand.dealer === 0;
    return (
      <BidPill
        bid={bidBySeat[seat]}
        tag={nameFor(seat)}
        coked={cokedSeats.has(seat)}
        showDealer={showDealerHere}
      />
    );
  };

  const centerLabel = (() => {
    if (frozenTrick) {
      const name = winnerSeat !== undefined ? nameFor(winnerSeat) : "";
      return (
        <div className="text-center">
          <div className="text-[10px] text-emerald-200 uppercase tracking-wide">Trick won by</div>
          <div className="text-emerald-300 font-bold text-sm leading-tight">{name}</div>
        </div>
      );
    }
    if (hand.phase === "playing") {
      return <div className="text-white/30 text-xs uppercase">Trick {hand.tricks.length + 1}/6</div>;
    }
    if (hand.phase === "bidding") {
      return (
        <div className="text-center">
          {highBid ? (
            <>
              <div className="text-[10px] text-white/40 uppercase tracking-wide">High bid</div>
              <div className="text-2xl font-bold text-emerald-300 leading-none">{highBid.value}</div>
              <div className="text-[10px] text-white/50 mt-0.5">by {nameFor(highBid.seat)}</div>
            </>
          ) : (
            <div className="text-white/30 text-xs uppercase">Bidding…</div>
          )}
        </div>
      );
    }
    if (hand.phase === "choosingTrump") {
      return <div className="text-white/30 text-xs uppercase text-center">Choosing trump…</div>;
    }
    return null;
  })();

  // Once the hand is over (and any trick-freeze has already played out),
  // replace the 3×3 seat grid with a full-width result panel so the table
  // itself shows the scoring breakdown.
  const showHandResult = !frozenTrick && hand.phase === "done";
  const bidderLabel =
    hand.bidder !== undefined ? nameFor(hand.bidder) : undefined;

  const tableBox =
    "min-h-[min(400px,52svh)] max-h-[min(520px,62svh)] w-full bg-white/5 rounded-2xl border border-white/10 p-3 sm:p-4 relative overflow-visible flex flex-col " +
    "min-[768px]:max-[1535px]:min-h-[min(480px,60svh)] min-[768px]:max-[1535px]:max-h-[min(680px,72svh)] " +
    "min-[768px]:max-[1535px]:p-5";

  return (
    <div className={tableBox}>
      {matchBanner ? (
        <div className="shrink-0 text-center pb-2 sm:pb-3 border-b border-white/10 mb-2">
          <div className="text-3xl font-bold">{matchBanner}</div>
        </div>
      ) : null}
      <div className="flex-1 min-h-0 flex flex-col items-stretch justify-start w-full">
        {showHandResult ? (
          <div className="flex-1 flex flex-col justify-center min-h-0 w-full">
            <HandResultCenter
              handNumber={hand.handNumber}
              bid={hand.bid}
              bidderLabel={bidderLabel}
              score={hand.score}
              abandoned={hand.abandoned}
              labelTeam={labelTeam}
            />
          </div>
        ) : inBidding ? (
          <FourSeatTrickLayout
            top={renderBidSeat(2)}
            left={renderBidSeat(1)}
            center={centerLabel}
            right={renderBidSeat(3)}
            bottom={renderBidSeat(0)}
          />
        ) : hand.phase === "choosingTrump" ? (
          <div className="flex flex-1 flex-col items-center justify-center w-full min-h-[min(260px,38svh)] min-[768px]:max-[1535px]:min-h-[min(340px,44svh)] px-2">
            {centerLabel}
          </div>
        ) : (
          <TrickPlaysAtSeats
            centerLabel={centerLabel}
            plays={plays}
            hideFrozenCards={!!hideFrozenCards}
            winnerSeat={winnerSeat}
            winnerDir={winnerDir}
            seatDir={s => SEAT_DIR[s]}
            nameFor={nameFor}
            showDealer={s => s === 0 && hand.dealer === 0}
          />
        )}
      </div>
    </div>
  );
}

function YourHand({
  hand, seat, ruleset, onPlay, showUndo, onUndo,
}: {
  hand: HandState;
  seat: SeatIndex;
  ruleset?: Ruleset;
  onPlay: (c: Card) => void;
  /** Solo quick play only — take back your last card before bots respond. */
  showUndo?: boolean;
  onUndo?: () => void;
}) {
  const cards = [...hand.hands[seat]].sort((a, b) => {
    if (a.suit !== b.suit) return a.suit.localeCompare(b.suit);
    return a.rank - b.rank;
  });

  /** After play, hands are cleared — reconstruct the six starters from `{ type: "play" }` log lines. */
  const replayFan = useMemo(() => {
    if (hand.phase !== "done" || cards.length > 0) return [];
    const fromLog = cardsPlayedFromLogForSeat(hand.eventLog, seat);
    return [...fromLog].sort((a, b) => {
      if (a.suit !== b.suit) return a.suit.localeCompare(b.suit);
      return a.rank - b.rank;
    });
  }, [hand.phase, hand.eventLog, seat, cards.length]);

  const fanCards = cards.length > 0 ? cards : replayFan;
  const showReplayHint = cards.length === 0 && replayFan.length > 0 && hand.phase === "done";

  const isPlayingPhase = hand.phase === "playing" && hand.turn === seat;
  const legal = isPlayingPhase ? legalPlays(hand, seat, ruleset) : [];
  const legalKey = new Set(legal.map(c => `${c.suit}${c.rank}`));

  return (
    <div className="relative">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-xs uppercase tracking-wide text-white/40">
          Your hand ({fanCards.length})
        </div>
        <div className="flex items-center gap-2">
          {isPlayingPhase && <TurnChip label="Your turn · next to act" />}
        </div>
      </div>
      {showUndo && onUndo && (
        <div className="relative z-30 mb-3">
          <button
            type="button"
            onClick={onUndo}
            title="Take back your play and any cards played after it — until it is your turn again"
            className="w-full max-w-xs mx-auto block sm:inline sm:w-auto text-sm font-semibold px-4 py-2 rounded-lg border-2 border-amber-400/70 text-amber-100 bg-amber-500/20 hover:bg-amber-500/30 shadow-[0_0_12px_rgba(251,191,36,0.15)] transition"
          >
            Undo play
          </button>
        </div>
      )}
      {fanCards.length > 0 ? (
        <div className="relative z-0">
          {showReplayHint ? (
            <div className="text-[10px] text-white/45 uppercase tracking-wide mb-1">
              Your cards this hand
            </div>
          ) : null}
          <HandFan
            cards={fanCards}
            legalKeys={legalKey}
            onPlay={onPlay}
            isPlayingPhase={isPlayingPhase}
          />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-white/15 px-4 py-5 text-center text-white/45 text-sm">
          {hand.phase === "done"
            ? "Hand complete — your cards refresh on the next deal."
            : "No cards left this hand."}
        </div>
      )}
    </div>
  );
}

function BiddingPanel({
  hand, seat, ruleset, onBid, onClaimCoke, compact,
}: {
  hand: HandState;
  seat: SeatIndex;
  ruleset: Ruleset;
  onBid: (v: BidValue | "pass") => void;
  onClaimCoke: () => void;
  compact?: boolean;
}) {
  const options = legalBids(hand, seat);
  const canCoke = canClaimCoke(hand, seat, ruleset);
  const pad = compact ? "p-2.5 sm:p-4" : "p-3 sm:p-4";
  const titleMb = compact ? "mb-1.5 sm:mb-3" : "mb-2 sm:mb-3";
  return (
    <div className={"bg-white/5 border border-white/10 rounded-xl " + pad}>
      <div className={"text-xs sm:text-sm font-medium text-white/90 " + titleMb}>Your bid:</div>
      <div className="flex gap-1.5 flex-wrap">
        {options.map(opt => (
          <button
            key={opt}
            onClick={() => onBid(opt)}
            className={
              "px-4 py-2 rounded-lg font-semibold " +
              (opt === "pass"
                ? "bg-white/10 hover:bg-white/20 text-white"
                : "bg-emerald-500 hover:bg-emerald-400 text-slate-900")
            }
          >
            {opt === "pass" ? "Pass" : `Bid ${opt}`}
          </button>
        ))}
        {canCoke && (
          <button
            onClick={onClaimCoke}
            className="px-4 py-2 rounded-lg font-semibold bg-amber-400 hover:bg-amber-300 text-slate-900"
            title="Your hand is all 3–9s. Take 6 new cards and sit out the bid."
          >
            Claim coke hand
          </button>
        )}
      </div>
    </div>
  );
}

function TrumpPanel({ onPick, compact }: { onPick: (s: Suit) => void; compact?: boolean }) {
  const pad = compact ? "p-2.5 sm:p-4" : "p-3 sm:p-4";
  const titleMb = compact ? "mb-1.5 sm:mb-3" : "mb-2 sm:mb-3";
  const btnY = compact ? "py-1.5 sm:py-2" : "py-2";
  return (
    <div className={"bg-white/5 border border-white/10 rounded-xl " + pad}>
      <div className={"text-xs sm:text-sm font-medium text-white/90 " + titleMb}>
        You won the bid — choose trump:
      </div>
      <div className="grid grid-cols-4 gap-1 sm:gap-1.5">
        {(["C", "D", "H", "S"] as Suit[]).map(s => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className={
              "min-w-0 flex flex-col items-center justify-center rounded-lg " +
              btnY +
              " px-0.5 sm:px-3 font-semibold text-[11px] sm:text-sm leading-tight transition-colors " +
              TRUMP_CHOICE_BUTTON_CLASS[s]
            }
          >
            <span className="text-lg sm:text-xl leading-none">{SUIT_SYMBOL[s]}</span>
            <span className="mt-0.5 truncate max-w-full">{SUIT_LABEL[s]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function HandScorePanel({ game, onNext }: { game: GameState; onNext: () => void }) {
  const h = game.currentHand;
  // The scoring breakdown now lives in the center of the table (see
  // TrickArea → HandResultCenter). This panel just surfaces the
  // bidder-set warning (if any) and the Deal-next-hand CTA.
  const setBack =
    !!h.score && !h.score.bidderMade && h.bidder !== undefined && h.bid;
  const bidderTeamLabel =
    h.bidder !== undefined && TEAM_OF[h.bidder] === "A" ? "Your team" : "Opponents";

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
      <div className="text-sm text-white/70">
        {h.abandoned ? (
          <>Hand {h.handNumber} abandoned — all passed or coked.</>
        ) : setBack ? (
          <span className="text-red-300">
            {bidderTeamLabel} was set back — lost {h.bid} points.
          </span>
        ) : (
          <>Hand {h.handNumber} complete.</>
        )}
      </div>
      <button
        onClick={onNext}
        className="px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold"
      >
        Deal next hand →
      </button>
    </div>
  );
}

function labelTeam(t?: "A" | "B"): string {
  if (t === "A") return "You";
  if (t === "B") return "Opp.";
  return "—";
}

function GameOverPanel({
  onPlayAgain, onHome,
}: { onPlayAgain: () => void; onHome: () => void }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
      <div className="flex flex-wrap gap-3 justify-center">
        <button
          type="button"
          onClick={onPlayAgain}
          className="px-5 py-2.5 rounded-lg bg-emerald-500 text-slate-900 font-semibold hover:bg-emerald-400"
        >
          Play again
        </button>
        <button
          type="button"
          onClick={onHome}
          className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white"
        >
          Home
        </button>
      </div>
    </div>
  );
}

function EventLogPeek({
  hand, players,
}: { hand: HandState; players: Record<SeatIndex, GameState["players"][number]> }) {
  const [open, setOpen] = useState(false);
  const recent = hand.eventLog.slice(-10);
  if (recent.length === 0) return null;
  return (
    <div className="mt-3 sm:mt-4 border-t border-white/10 pt-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-xs text-white/45 hover:text-white/75 underline-offset-2 hover:underline w-full text-left"
      >
        {open ? "Hide recent plays" : "Show recent plays"}
        <span className="text-white/30 ml-1">({recent.length})</span>
      </button>
      {open && (
        <ul className="mt-2 space-y-1 text-xs text-white/50 list-none pl-0">
          {recent.map((e, i) => (
            <li key={i} className="leading-snug">{formatEvent(e, players)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatEvent(
  e: HandState["eventLog"][number],
  players: Record<SeatIndex, GameState["players"][number]>,
): string {
  const who = (seat: SeatIndex) => (seat === 0 ? "You" : players[seat].screenName);
  switch (e.type) {
    case "deal": return `Deal (seed ${e.seed}) — dealer ${who(e.dealer)}`;
    case "bid": return `${who(e.seat)}: ${e.value === "pass" ? "pass" : `bid ${e.value}`}`;
    case "forcedBid": return `${who(e.seat)} forced to bid 2`;
    case "chooseTrump": return `${who(e.seat)} picks ${SUIT_LABEL[e.trump]} trump`;
    case "play": return `${who(e.seat)} plays ${rankLabel(e.card.rank)}${SUIT_SYMBOL[e.card.suit]}`;
    case "trickWon": return `Trick won by ${who(e.winner)}`;
    case "score":
      return `Score: You ${e.score.teamA >= 0 ? "+" : ""}${e.score.teamA}, Opp ${e.score.teamB >= 0 ? "+" : ""}${e.score.teamB}`;
    case "cokeClaim": return `${who(e.seat)} claimed a coke hand — redealt`;
    case "abandoned": return "Hand abandoned — all passed / coked";
  }
}

// ============================================================
// Bot driver helpers
// ============================================================

function currentActor(hand: HandState): SeatIndex | null {
  if (hand.phase === "bidding") return hand.turn ?? null;
  if (hand.phase === "choosingTrump") return hand.bidder ?? null;
  if (hand.phase === "playing") return hand.turn ?? null;
  return null;
}

function stepBot(game: GameState, seat: SeatIndex): GameState {
  const hand = game.currentHand;
  const seatPlayer = game.players.find(p => p.seat === seat);
  if (!seatPlayer?.botDifficulty) return game;
  const diff = normalizeDifficulty(seatPlayer.botDifficulty);

  if (hand.phase === "bidding") {
    // Vacation ruleset: if the bot is sitting on a coke hand, always claim it.
    // Dealing fresh cards is strictly better than bidding on 3–9 trash.
    if (canClaimCoke(hand, seat, game.ruleset)) {
      return { ...game, currentHand: applyClaimCokeLocal(hand, seat, game.ruleset) };
    }
    const value = botBid(hand, seat, diff);
    return { ...game, currentHand: applyBid(hand, { seat, value }, game.ruleset) };
  }
  if (hand.phase === "choosingTrump" && hand.bidder === seat) {
    const trump = botChooseTrump(hand, seat);
    return { ...game, currentHand: applyChooseTrump(hand, seat, trump) };
  }
  if (hand.phase === "playing") {
    const card = botPlay(hand, seat, diff, game.ruleset);
    return { ...game, currentHand: applyPlay(hand, seat, card, game.ruleset) };
  }
  return game;
}

